import { useCallback, useEffect, useRef, useState } from 'react'
import { KENO_OPPOSITE } from '@nexbit/types'
import { useT } from '@/i18n'
import { useBoard } from '@/lib/board'
import { api, newIdempotencyKey, placeErrorKey } from '@/lib/api'
import { BET_OF, displayNumeric, ticketRef, type OddCode } from '@/lib/keno-view'
import { NumberInput } from './NumberInput'
import { Popper } from './Popper'
import type { Product } from './GameLot'

type Placed = { ok: true; ticket: string } | { ok: false; key: string; detail: string }

const CHIPS = [20, 100, 500, 2500]

/** One bet cell and its bet popover — the reference BetLot. */
export function BetLot({ product, type, range }: { product: Product; type: OddCode; range?: string }) {
  const t = useT()
  const board = useBoard()
  const betType = BET_OF[type]
  const odd = product.payoutRates[betType]
  const winner = product.winners[type] === true
  const stakeHere = product.roundId ? board.stakes[product.roundId]?.[betType] ?? 0 : 0
  const has = displayNumeric(stakeHere, 0)

  const [open, setOpen] = useState(false)
  const [stake, setStake] = useState<number | ''>('')
  const [error, setError] = useState<string | null>(null)
  const [betting, setBetting] = useState(false)
  const [placed, setPlaced] = useState<Placed | null>(null)
  const [snacking, setSnacking] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const okButton = useRef<HTMLButtonElement>(null)
  const errorTimer = useRef<number | undefined>(undefined)
  const closeTimer = useRef<number | undefined>(undefined)

  const { min, max } = board.tier
  const desktop = () => document.documentElement.clientWidth > 767

  const close = useCallback(() => {
    setOpen(false)
    setStake('')
    setError(null)
    window.clearTimeout(closeTimer.current)
    window.clearTimeout(errorTimer.current)
  }, [])

  const flashError = useCallback((key: string) => {
    window.clearTimeout(errorTimer.current)
    setError(key)
    errorTimer.current = window.setTimeout(() => setError(null), 3000)
  }, [])

  // A new draw, or betting closing, takes the popover away — unless a submit is in flight.
  useEffect(() => { close() }, [product.drawId, close])
  useEffect(() => {
    if (product.status !== 'start-betting' && !betting) close()
  }, [product.status, betting, close])

  useEffect(() => () => {
    window.clearTimeout(closeTimer.current)
    window.clearTimeout(errorTimer.current)
  }, [])

  function openPopper() {
    if (product.status !== 'start-betting' || !odd) return
    const opposite = KENO_OPPOSITE[betType]
    if (opposite && product.roundId && (board.stakes[product.roundId]?.[opposite] ?? 0) > 0) {
      setSnacking(true)
      window.setTimeout(() => setSnacking(false), 2000)
      return
    }
    setBetting(false)
    setPlaced(null)
    setError(null)
    setStake(board.lastStake)
    setOpen(true)
    window.setTimeout(() => { if (desktop()) input.current?.focus() }, 25)
  }

  function changeStake(v: number | '') {
    setStake(v)
    if (v !== '' && v > max) {
      flashError('ERROR_MAX_TO')
      window.setTimeout(() => setStake(max), 20)
    }
  }

  function addStake(n: number) {
    setStake((s) => {
      const next = (s === '' ? 0 : s) + n
      if (next > max) {
        flashError('ERROR_MAX_TO')
        return max
      }
      return next
    })
    if (desktop()) input.current?.focus()
  }

  /** The reference checkError(): fix the amount up and say why, or let it through. */
  function checkError(): boolean {
    if (stake === '' || stake === 0) { flashError('ERROR_TO'); setStake(min); return true }
    if (stake < min) { flashError('ERROR_MIN_TO'); setStake(min); return true }
    if (stake > max) { flashError('ERROR_MAX_TO'); setStake(max); return true }
    if (board.balance !== null && stake > board.balance) { flashError('ERROR_XAMOUNT'); return true }
    return false
  }

  async function placeBet() {
    if (!product.roundId || checkError() || stake === '') return
    const amount = stake
    setBetting(true)
    setPlaced(null)
    try {
      const res = await api.placeBet(
        { roundId: product.roundId, betType, amount: String(amount), limitTier: board.tier.index },
        newIdempotencyKey(),
      )
      board.setBalance(Number(res.newBalance))
      board.setLastStake(amount)
      board.addStake(product.roundId, betType, amount)
      setPlaced({ ok: true, ticket: ticketRef(res.bet.ticketNo, res.bet.id) })
    } catch (err) {
      setPlaced({ ok: false, key: placeErrorKey(err), detail: err instanceof Error ? err.message : '' })
    }
    window.setTimeout(() => okButton.current?.focus(), 25)
    closeTimer.current = window.setTimeout(close, 5000)
  }

  const cell = (active: boolean, onClick: () => void) => (
    <a onClick={onClick} className={['odd', active ? 'active' : '', winner ? 'winner' : ''].filter(Boolean).join(' ')}>
      <div className="oddtxtbetname">{t(type)}</div>{' '}
      <span className={['stake', has ? 'active' : ''].filter(Boolean).join(' ')}>{has}</span>{' '}
      <div className="infobox">
        {range && <span className="rangetxt">{range}</span>}{' '}
        <span className="oddtxt">{odd !== undefined ? odd.toFixed(2) : ''}</span>
      </div>
    </a>
  )

  const id = `input-stake-${product.drawId}-${type.slice(1)}`

  return (
    <div className="cell betlot" {...{ type }}>
      {!open && cell(false, openPopper)}{' '}
      {open && (
        <Popper
          boundaryId={`gamecont-${product.gameId}`}
          onDocumentClick={() => { if (!betting || placed) close() }}
          updateKey={`${error}-${betting}-${placed ? 1 : 0}`}
          reference={cell(true, close)}
        >
          <div className="popper-header" style={{ paddingBottom: 0 }}>
            <span className="counter">{product.displayCountDown}</span>{' '}
            <b>{product.title}</b><br />
            {t('DRAW_NO')} {product.gCode}{product.drawId}
          </div>{' '}
          <form style={{ paddingTop: 0 }} onSubmit={(e) => { e.preventDefault(); void placeBet() }}>
            <div style={{ fontSize: 10 }}>{t(`${type}group`)}</div>{' '}
            <b>{t(type)}</b> @ <span className="bet-odds">{odd?.toFixed(2)}</span>{' '}
            {betting ? (
              <div>
                {placed ? (
                  <div className="result">
                    {placed.ok ? (
                      <span className="success">{t('PLACE_SUCCESS')}<br /><small>[{t('REF')}: {placed.ticket}]</small></span>
                    ) : (
                      <span className="failed">{t(placed.key)} [{placed.detail}]</span>
                    )}
                  </div>
                ) : (
                  <div className="loading"><div className="loading-icon" /></div>
                )}
              </div>
            ) : (
              <div>
                <div>
                  <div className="inputs">
                    <NumberInput
                      ref={input}
                      id={id}
                      value={stake}
                      onChange={changeStake}
                      onReset={() => { setStake(''); setError(null) }}
                      onEscape={close}
                    />
                  </div>{' '}
                  <div className="quickbet grid-x small-up-4">
                    {CHIPS.map((c) => (
                      <div className="cell" key={c}><a onClick={() => addStake(c)}>+{c.toLocaleString('en-US')}</a></div>
                    ))}{' '}
                    <div className="cell"><a onClick={() => { setStake(max); if (desktop()) input.current?.focus() }} className="max">{t('MAX')}</a></div>
                  </div>
                </div>{' '}
                <div className="summary">
                  {error && <div className="error">{t(error)}</div>}{' '}
                  {t('PAYOUT')}: <span className="summary_value">{stake === '' || odd === undefined ? '' : displayNumeric(stake * odd, 2)}</span><br />
                  {t('MIN_TO')}: <span className="summary_value">{displayNumeric(min, 0, true)}</span><br />
                  {t('MAX_TO')}: <span className="summary_value">{displayNumeric(max, 0, true)}</span><br />
                </div>
              </div>
            )}{' '}
            {placed ? (
              <button ref={okButton} type="reset" onClick={close} className="ok-button">OK</button>
            ) : !betting ? (
              <div className="bet-choices">
                <button type="reset" onClick={close} className="cancel-button">{t('CANCEL')}</button>{' '}
                <button type="submit" className="bet-button">{t('BET')}</button>
              </div>
            ) : null}
          </form>
        </Popper>
      )}{' '}
      <div className={['ui-snackbar-container', snacking ? 'show' : '', 'lose'].filter(Boolean).join(' ')}>
        <div className="ui-snackbar">Cannot bet on both side.
        </div>
      </div>
    </div>
  )
}
